/**
 * ArrivalDialog - Mark an aircraft as returned
 *
 * Simple confirmation dialog. Creates an 'arrived' event and
 * resolves any active allocation_schedule.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useCreateArrivalEvent } from '@/hooks/useAllocationEvents'

interface ArrivalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tailNumber: string
  allocationId: string
  aircraftId: string
  locationId: string
}

export function ArrivalDialog({
  open,
  onOpenChange,
  tailNumber,
  allocationId,
  aircraftId,
  locationId,
}: ArrivalDialogProps) {
  const [notes, setNotes] = useState('')
  const arrivalEvent = useCreateArrivalEvent()

  const handleConfirm = async () => {
    try {
      await arrivalEvent.mutateAsync({
        allocation_id: allocationId,
        aircraft_id: aircraftId,
        location_id: locationId,
        notes: notes || null,
      })
      toast.success(`${tailNumber} marked as returned`)
      setNotes('')
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to record arrival')
      console.error('Arrival event error:', err)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark {tailNumber} as Returned</AlertDialogTitle>
          <AlertDialogDescription>
            Confirm that {tailNumber} has returned and is back on the field.
            This will update the aircraft status to &quot;Present&quot; and resolve any active schedule.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="arr-notes" className="text-xs">Notes</Label>
            <Textarea
              id="arr-notes"
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[60px]"
              rows={2}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setNotes('')}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={arrivalEvent.isPending}
          >
            {arrivalEvent.isPending ? 'Recording...' : 'Confirm Arrival'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
